import { create } from 'zustand';

/**
 * The getting-started checklist.
 *
 * Shown on an empty board to someone who has not used one before, ticking off
 * as they do each thing. Progress is recorded against the account rather than
 * the browser, so it teaches once instead of reappearing on every new device —
 * and it never comes back for someone who already knows the ropes.
 *
 * Kept out of the board store deliberately: it is coaching, not document state,
 * and nothing here should end up in an undo history or on the wire.
 */

export type Step = 'note' | 'image' | 'group' | 'zoom';

export const STEPS: { key: Step; title: string; how: string }[] = [
  { key: 'note', title: 'Add a note', how: 'Double-click the canvas' },
  { key: 'image', title: 'Place an image', how: 'Drag a file in, or an asset tile' },
  { key: 'group', title: 'Group items', how: 'Select 2+, press ⌘G' },
  { key: 'zoom', title: 'Zoom around', how: '⌘ scroll, or − / + below' },
];

type OnboardingStore = {
  /** Whether the panel is on screen. */
  active: boolean;
  done: Record<Step, boolean>;
  /** True for the moment between the last tick and the panel leaving. */
  finishing: boolean;

  /** Show the checklist. Called when an empty board opens for a new user. */
  start: () => void;
  mark: (step: Step) => void;
  dismiss: () => void;
  finish: () => void;
};

const nothingDone = (): Record<Step, boolean> => ({ note: false, image: false, group: false, zoom: false });

export const useOnboarding = create<OnboardingStore>((set, get) => ({
  active: false,
  done: nothingDone(),
  finishing: false,

  start: () => set({ active: true, done: nothingDone(), finishing: false }),

  mark: (step) => {
    const state = get();
    // Marking is called from ordinary editing paths, so it has to be cheap and
    // silent when the checklist is not showing.
    if (!state.active || state.done[step]) return;
    set({ done: { ...state.done, [step]: true } });
  },

  dismiss: () => set({ active: false, finishing: false }),
  finish: () => set({ finishing: true }),
}));

/** Convenience for the editing paths, which should not care whether it is on. */
export const markStep = (step: Step): void => useOnboarding.getState().mark(step);
